import { HandHeart, LayoutGrid, LogOut } from "lucide-react";
import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { AREA_SEGMENT, NAV_BY_ROLE, type NavItem } from "./nav";
import { NotificationBell } from "./NotificationBell";
import { useAuth } from "../hooks/useAuth";
import type { UserRole } from "../api/types";

const ROLE_LABEL: Record<UserRole, string> = {
  SUPER_ADMIN: "Super Admin",
  TENANT_ADMIN: "Admin Tenant",
  THERAPIST: "Terapis",
  PATIENT: "Pasien",
};

function Brand() {
  const { isAuthed, homePath } = useAuth();
  return (
    <Link to={isAuthed ? homePath : "/"} className="brand">
      <span className="brand-mark" aria-hidden>
        <HandHeart size={18} />
      </span>
      <span className="brand-name">Totok</span>
    </Link>
  );
}

function Sidebar({ items }: { items: NavItem[] }) {
  return (
    <aside className="sidebar">
      <Brand />
      <nav className="sidebar-nav">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}
          >
            <item.icon size={18} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-foot">
        <p>Reservasi Totok Punggung</p>
      </div>
    </aside>
  );
}

export function RoleArea({ role }: { role: UserRole }) {
  const { user, isLoading, isAuthed, homePath, logoutNow } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="app-loading" role="status">
        <span className="spinner" />
      </div>
    );
  }
  if (!isAuthed) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!user || user.role !== role) {
    return <Navigate to={homePath} replace />;
  }

  const items = NAV_BY_ROLE[role];
  const segment = AREA_SEGMENT[role];
  const mobileItems = items.slice(0, 4);
  const hasMore = items.length > 4;
  const isMenuPage = location.pathname === `/${segment}/menu`;

  return (
    <div className="app">
      <Sidebar items={items} />

      <div className="app-main">
        <header className="topbar">
          <div className="topbar-left">
            <span className="brand-name">{ROLE_LABEL[role]}</span>
          </div>
          <div className="topbar-right">
            <NotificationBell segment={segment} />
            <span className="topbar-user">{user.fullName}</span>
            <button type="button" className="btn-icon" onClick={() => void logoutNow()} title="Keluar">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>

        {!isMenuPage ? (
          <nav className="bottom-nav">
            {mobileItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `bottom-link ${isActive ? "bottom-link-active" : ""}`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            ))}
            {hasMore ? (
              <NavLink
                to={`/${segment}/menu`}
                className={({ isActive }) => `bottom-link ${isActive ? "bottom-link-active" : ""}`}
              >
                <LayoutGrid size={20} />
                <span>Menu</span>
              </NavLink>
            ) : null}
          </nav>
        ) : null}
      </div>
    </div>
  );
}
